import { useContext } from 'react';

import { UserContext } from '~/components/providers/UserProvider';
import { objectToTuples } from '~/services/utils';
import type { IPostWalletsPaymentsHandlesVIPPPayload } from '~/services/wallet/interfaces/wallets.interface';
import { PaymentType } from '~/services/wallet/interfaces/wallets.interface';

const apiKey = process.env.NEXT_PUBLIC_PAYSAFE_API_KEY;
const environment = process.env.NEXT_PUBLIC_PAYSAFE_ENVIRONMENT;
const accountId = parseInt(process.env.NEXT_PUBLIC_PAYSAFE_VIPP_ACCOUNT, 10);

function useWithdrawalWithVIPP({
  amount,
  merchantRefNum,
  query = null,
}: IPostWalletsPaymentsHandlesVIPPPayload) {
  const { userDetails, user } = useContext(UserContext);

  const getToken: () => Promise<string> = async () => {
    const isUsingQuery = query && Object.entries(query).length > 0;
    const dateOfBirth = new Date(userDetails?.dateOfBirth);
    const retryTimeout = setTimeout(() => getToken(), 500);
    if (!window.paysafe) {
      // TODO: Update this to something better...Help Czech friends!
      return retryTimeout as unknown as string;
    }
    clearTimeout(retryTimeout);

    const params = isUsingQuery
      ? {
          amountInCents: Number(query?.amountInCents),
          firstName: query?.firstName,
          lastName: query?.lastName,
          email: query?.email,
          phone: query?.phone.slice(2, query?.phone.length),
          dobDay: Number(query?.dobDay),
          dobMonth: Number(query?.dobMonth),
          dobYear: Number(query?.dobYear),
          address: query?.address,
          city: query?.city,
          zip: query?.zip,
          state: query?.state.toUpperCase(),
          country: query?.country,
        }
      : {
          amountInCents: amount * 100,
          firstName: userDetails?.firstName,
          lastName: userDetails?.lastName,
          email: user?.email,
          phone: userDetails?.mobileNumber.slice(2, userDetails?.mobileNumber.length),
          dobDay: dateOfBirth?.getUTCDate(),
          dobMonth: dateOfBirth.getUTCMonth() + 1,
          dobYear: dateOfBirth?.getUTCFullYear(),
          address: userDetails?.address,
          city: userDetails?.city,
          zip: userDetails?.zip,
          state: userDetails?.state.toUpperCase(),
          country: userDetails?.country,
        };

    const {
      amountInCents,
      firstName,
      lastName,
      email,
      phone,
      dobDay,
      dobMonth,
      dobYear,
      address,
      city,
      zip,
      state,
      country,
    } = params;

    const values = {
      paymentType: PaymentType.VIPPREFERRED,
      currency: 'USD',
      amount: amountInCents,
      payout: true,
      payoutConfig: {
        maximumAmount: 100000000,
      },
      locale: 'en_US',
      simulator: 'EXTERNAL',
      environment,
      customer: {
        firstName,
        lastName,
        email,
        phone,
        dateOfBirth: {
          day: dobDay,
          month: dobMonth,
          year: dobYear,
        },
      },
      billingAddress: {
        street: address,
        city,
        zip,
        country,
        state,
      },
      merchantRefNum,
      canEditAmount: false,
      paymentMethodDetails: {
        vippreferred: {
          accountId,
          registrationId: userDetails?.vippRegistrationId,
        },
      },
      merchantDescriptor: {
        dynamicDescriptor: 'Splashsports',
        phone: '1234567890',
      },
      displayPaymentMethods: ['vippreferred'],
      threeDs: {
        merchantUrl: window.location.href,
        messageCategory: 'PAYMENT',
        authenticationPurpose: 'PAYMENT_TRANSACTION',
        deviceChannel: 'BROWSER',
      },
    };

    // According to global interface checkout does not have setup function
    return new Promise((resolve, reject) => {
      (window as any).paysafe?.checkout?.setup(
        apiKey,
        values,
        (instance, _, result) => {
          if (result?.paymentHandleToken) {
            instance.showSuccessScreen();
            if (isUsingQuery) {
              const nativeAppQuery = new URLSearchParams(
                objectToTuples({
                  paymentHandleToken: result.paymentHandleToken,
                  merchantRefNum,
                })
              ).toString();

              window.location.href = `${process.env.NEXT_PUBLIC_NATIVE_APP_VIPP}://onlinebanking?${nativeAppQuery}`;
              return;
            }
            setTimeout(() => instance.close(), 3000);
            resolve(result.paymentHandleToken as string);
          } else if (instance) {
            reject(new Error('Could not get token'));
          }
        },
        () => {
          if (isUsingQuery) {
            const nativeAppQuery = new URLSearchParams(
              objectToTuples({ error: 'User closed VIPP Modal' })
            ).toString();
            window.location.href = `${process.env.NEXT_PUBLIC_NATIVE_APP_VIPP}://onlinebanking?${nativeAppQuery}`;
            return;
          }
          reject();
        }
      );
    });
  };

  return getToken;
}

export { useWithdrawalWithVIPP };
